import type { GlyphId } from '../engine';

/**
 * Hand-drawn glyph set, in the spirit of ochre strokes on a cave wall.
 * Every path lives on a 100×100 grid and is stroked, never filled, so the
 * whole set recolours from CSS with one property.
 */
const PATHS: Record<GlyphId, string> = {
  leaf: 'M50 88 C50 60 50 40 50 16 M50 30 C30 26 18 40 20 58 C36 60 48 50 50 34 M50 44 C70 40 82 54 80 72 C64 74 52 64 50 48',
  stone: 'M22 62 L34 28 L64 20 L82 44 L74 76 L40 82 Z M34 28 L52 50 L82 44 M52 50 L40 82',
  grass: 'M22 84 C28 58 34 42 30 22 M40 86 C46 54 48 34 46 18 M58 86 C62 56 68 40 66 24 M76 84 C78 62 84 46 82 30',
  eye: 'M10 52 C28 26 72 26 90 52 C72 78 28 78 10 52 Z M50 38 A14 14 0 1 0 50 66 A14 14 0 1 0 50 38',
  flame: 'M50 90 C24 82 18 58 34 38 C38 48 44 50 46 44 C48 28 40 22 44 10 C70 22 84 46 76 66 C71 79 62 87 50 90 Z',
  coal: 'M18 68 L32 44 L58 40 L74 58 L66 80 L30 84 Z M40 22 L52 16 M62 26 L72 18 M50 34 L46 22',
  deer: 'M34 88 L36 56 L64 54 L66 86 M36 56 C30 44 34 34 46 32 L58 30 M46 32 L38 14 L28 20 M46 32 L52 12 L62 18 M58 30 L66 40 L80 38',
  hide: 'M28 20 C14 34 16 56 26 74 C40 86 62 88 76 76 C88 60 86 34 72 20 C58 12 42 12 28 20 Z M50 20 L50 78',
  bone: 'M28 30 A11 11 0 1 0 34 44 L66 60 A11 11 0 1 0 72 74 A11 11 0 1 0 66 60 M34 44 A11 11 0 1 0 28 30',
  leather: 'M20 30 L80 24 L84 70 L24 78 Z M20 30 L84 70 M80 24 L24 78',
  tree: 'M50 90 L50 52 M50 62 L30 44 M50 58 L70 40 M50 52 C28 50 18 34 30 20 C36 10 52 8 58 18 C74 12 84 30 72 44 C66 52 58 53 50 52 Z',
  thread: 'M24 22 C58 30 42 48 24 54 C58 62 42 78 24 86 M76 22 C42 30 58 48 76 54 C42 62 58 78 76 86',
  hand: 'M32 88 L30 52 C30 44 40 44 40 52 L40 34 C40 26 50 26 50 34 L50 30 C50 22 60 22 60 30 L60 38 C60 30 70 30 70 38 L70 66 C70 80 62 88 52 88 Z',
  spiral: 'M50 50 C44 50 44 58 50 58 C60 58 62 46 52 42 C38 36 30 52 40 64 C52 78 74 66 72 46 C70 24 44 14 26 30',
  grain: 'M50 90 L50 34 M50 46 C36 44 30 34 32 22 C46 22 52 32 50 44 M50 40 C64 38 70 28 68 16 C54 16 48 26 50 38 M50 30 C44 22 46 12 50 6 C54 12 56 22 50 30',
  clay: 'M26 40 C26 24 74 24 74 40 C74 56 82 62 78 76 C74 88 26 88 22 76 C18 62 26 56 26 40 Z M26 40 C40 48 60 48 74 40',
  pot: 'M30 34 L70 34 M26 42 C26 70 34 84 50 84 C66 84 74 70 74 42 M34 54 L66 54 M38 66 L62 66',
  skull: 'M28 44 C28 22 72 22 72 44 C72 54 68 60 66 66 L34 66 C32 60 28 54 28 44 Z M34 66 L36 82 L64 82 L66 66 M42 42 A6 6 0 1 0 42 43 M58 42 A6 6 0 1 0 58 43',
  spear: 'M24 88 L70 30 M70 30 L58 26 L64 14 L82 10 L78 28 Z M40 60 L52 66',
  home: 'M18 52 L50 20 L82 52 M28 46 L28 84 L72 84 L72 46 M42 84 L42 62 L58 62 L58 84',
  water: 'M14 40 C24 30 34 50 44 40 C54 30 64 50 74 40 C80 34 84 36 88 40 M14 62 C24 52 34 72 44 62 C54 52 64 72 74 62 C80 56 84 58 88 62',
  mountain: 'M8 80 L34 32 L52 62 L64 44 L92 80 Z M34 32 L26 46 L42 46 Z',
  moon: 'M64 12 C36 20 24 46 34 68 C42 84 60 90 74 84 C50 74 40 50 50 30 C54 22 58 16 64 12 Z',
  tribe: 'M24 84 L24 62 C24 52 40 52 40 62 L40 84 M32 34 A9 9 0 1 0 32 52 A9 9 0 1 0 32 34 M60 84 L60 58 C60 46 80 46 80 58 L80 84 M70 28 A10 10 0 1 0 70 48 A10 10 0 1 0 70 28 M46 86 L46 70 C46 62 56 62 56 70 L56 86',
  ember: 'M50 86 C30 80 22 62 32 46 C36 54 42 54 42 48 C42 34 38 28 42 18 C62 28 74 46 68 62 C64 74 58 82 50 86 Z M50 70 A8 8 0 1 0 50 71',
};

const FALLBACK = 'M20 50 A30 30 0 1 0 80 50 A30 30 0 1 0 20 50 M50 32 L50 56 M50 66 L50 68';

export type GlyphTone = 'ochre' | 'bone' | 'moss';

interface GlyphProps {
  id: GlyphId;
  size?: number;
  tone?: GlyphTone;
  title?: string;
}

export function Glyph({ id, size = 22, tone = 'ochre', title }: GlyphProps) {
  const toneClass = tone === 'bone' ? ' bone-stroke' : tone === 'moss' ? ' moss-stroke' : '';
  return (
    <svg
      className={`glyph${toneClass}`}
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[id] ?? FALLBACK} />
    </svg>
  );
}
